/**
 * White-label branding API client (XC-ERP1-U11, read/write contract §9 D9).
 *
 * Routed through the gateway (/api/v1/tenants/current/branding). The tenant is
 * always the JWT's: no call carries a tenant id or an object path, and the
 * operator Pipeline tenant override is never attached (branding is the home
 * tenant's, read and write alike). Writes need MANAGE_TENANT and the
 * ``white_label`` entitlement; the gateway enforces both.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import axios from 'axios';

const brandingApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

brandingApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

export const BRANDING_PATH = '/api/v1/tenants/current/branding';

export type BrandingKind = 'logo' | 'wingo_default';
export type BrandingDisplayMode = 'plain' | 'light_badge';

export interface BrandingHistoryEntry {
  version: number;
  created_at: string;
  kind: BrandingKind;
}

export interface TenantBranding {
  entitled: boolean;
  kind: BrandingKind;
  version: number | null;
  /** Signed GCS URL, valid 60 minutes; null unless a logo is live. */
  logo_url: string | null;
  display_mode: BrandingDisplayMode;
  company_name: string;
  history: BrandingHistoryEntry[];
}

export type ContrastVerdict = 'ok' | 'low';

/** The background-removed variant's checks (D9 extension, advisor 10-03). */
export interface LogoRemovalChecks {
  contrast: ContrastVerdict;
  light_badge_suggested: boolean;
  /** Margins trimmed from the removed variant, ``[left, top, right, bottom]`` in source pixels. */
  trimmed_margins: [number, number, number, number];
}

/** Top-level fields describe the image as uploaded; ``removal`` the removed variant. */
export interface LogoPreviewChecks {
  transparent: boolean;
  contrast: ContrastVerdict;
  light_badge_suggested: boolean;
  removal_available: boolean;
  /** Null when ``removal_available`` is false (or the verdict is missing or malformed). */
  removal: LogoRemovalChecks | null;
}

export interface LogoPreview {
  preview_token: string;
  preview_data_url: string;
  removed_background_data_url: string | null;
  checks: LogoPreviewChecks;
}

export interface SaveLogoRequest {
  preview_token: string;
  use_removed_background: boolean;
  display_mode: BrandingDisplayMode;
}

export type RestoreBrandingRequest = { version: number } | { wingo_default: true };

/** The current tenant's branding (any signed-in member). */
export async function fetchTenantBranding(): Promise<TenantBranding> {
  const { data } = await brandingApi.get<TenantBranding>(BRANDING_PATH);
  return data;
}

const isMargin = (m: unknown): m is number =>
  typeof m === 'number' && Number.isFinite(m) && m >= 0;

/**
 * The removed variant's verdict as the card may read it, or null. A verdict the
 * card cannot trust is null, so it is shown as unchecked, never as passing.
 */
export function toRemovalChecks(
  raw: unknown,
  removalAvailable: boolean,
): LogoRemovalChecks | null {
  if (!removalAvailable || typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (r.contrast !== 'ok' && r.contrast !== 'low') return null;
  if (typeof r.light_badge_suggested !== 'boolean') return null;
  const m = r.trimmed_margins;
  if (!Array.isArray(m) || m.length !== 4 || !m.every(isMargin)) return null;
  return {
    contrast: r.contrast,
    light_badge_suggested: r.light_badge_suggested,
    trimmed_margins: [m[0], m[1], m[2], m[3]],
  };
}

/** Upload a logo for the checks; nothing is stored until ``saveLogo``. */
export async function previewLogo(file: File): Promise<LogoPreview> {
  const form = new FormData();
  form.append('file', file);
  // Let the browser set the multipart boundary.
  const { data } = await brandingApi.post<LogoPreview>(`${BRANDING_PATH}/logo/preview`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const checks = data.checks;
  return {
    ...data,
    checks: { ...checks, removal: toRemovalChecks(checks?.removal, !!checks?.removal_available) },
  };
}

/** Store the previewed logo as a new version. Sends exactly the contract fields. */
export async function saveLogo(req: SaveLogoRequest): Promise<TenantBranding> {
  const body: SaveLogoRequest = {
    preview_token: req.preview_token,
    use_removed_background: req.use_removed_background,
    display_mode: req.display_mode,
  };
  const { data } = await brandingApi.post<TenantBranding>(`${BRANDING_PATH}/logo`, body);
  return data;
}

/** Restore an earlier version or the Wingo logo; either appends a new version. */
export async function restoreBranding(req: RestoreBrandingRequest): Promise<TenantBranding> {
  const body: RestoreBrandingRequest =
    'wingo_default' in req && req.wingo_default === true
      ? { wingo_default: true }
      : { version: (req as { version: number }).version };
  const { data } = await brandingApi.post<TenantBranding>(`${BRANDING_PATH}/restore`, body);
  return data;
}

/**
 * The signed logo as a data URL, for the no-flash header cache. The signed URL
 * is its own credential: no cookies and no bearer token go to storage.
 */
export async function fetchLogoAsDataUrl(url: string): Promise<string> {
  const resp = await fetch(url, { credentials: 'omit', mode: 'cors' });
  if (!resp.ok) throw new Error(`logo fetch failed: ${resp.status}`);
  const type = (resp.headers.get('content-type') ?? '').split(';')[0].trim().toLowerCase();
  if (!/^image\/(png|webp|jpeg)$/.test(type)) {
    throw new Error(`logo fetch returned ${type || 'no type'}`);
  }
  const bytes = new Uint8Array(await resp.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return `data:${type};base64,${btoa(binary)}`;
}
